import React from "react";
import { Box, Button } from "@mui/material";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

const plans = [
  {
    name: "Starter",
    titleKey: "landing_recruiter.prices.starter2026.title",
    priceKey: "landing_recruiter.prices.starter2026.annonce_price",
    bulletpointsKey: "landing_recruiter.prices.starter2026.bulletpoints",
    offerBulletpointsKey: null,
    popular: false,
  },
  {
    name: "Business",
    titleKey: "landing_recruiter.prices.business2026.title",
    priceKey: "landing_recruiter.prices.business2026.annonce_price",
    bulletpointsKey: "landing_recruiter.prices.business2026.bulletpoints",
    offerBulletpointsKey:
      "landing_recruiter.prices.business2026.offer_bulletpoints",
    popular: true,
  },
  {
    name: "Pro",
    titleKey: "landing_recruiter.prices.pro2026.title",
    priceKey: "landing_recruiter.prices.pro2026.annonce_price",
    bulletpointsKey: "landing_recruiter.prices.pro2026.bulletpoints",
    offerBulletpointsKey:
      "landing_recruiter.prices.pro2026.offer_bulletpoints",
    popular: false,
  },
];

const BulletItem = ({ children, isBold }) => (
  <Box
    component="li"
    sx={{
      listStyle: "none",
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      fontSize: "0.875rem",
      fontFamily: isBold
        ? "Segoe UI Bold, system-ui, sans-serif"
        : "Segoe UI Regular, system-ui, sans-serif",
      color: isBold ? "hsl(220, 25%, 12%)" : "hsl(220, 10%, 46%)",
      mb: "12px",
    }}
  >
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        mt: "2px",
        color: "hsl(145, 63%, 42%)",
        display: "flex",
      }}
    >
      <Check size={14} />
    </Box>
    <Box component="span">{children}</Box>
  </Box>
);

const OfferBulletItem = ({ children }) => (
  <Box
    component="li"
    sx={{
      listStyle: "none",
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      fontSize: "0.875rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      color: "hsl(220, 10%, 46%)",
      mb: "12px",
    }}
  >
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        mt: "2px",
        color: "hsl(145, 63%, 42%)",
        display: "flex",
      }}
    >
      <Check size={14} />
    </Box>
    <Box component="span">
      {children}
      <Box
        component="b"
        sx={{
          ml: "8px",
          color: "hsl(145, 63%, 42%)",
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        }}
      >
        <FormattedMessage id="landing_recruiter.prices.offer" />
      </Box>
    </Box>
  </Box>
);

const PricingSection = ({ useMailto = false, showPrices = false }) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <Box
      component="section"
      id="pricing"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(0, 0%, 100%)",
      }}
    >
      <Box sx={{ maxWidth: "1152px", mx: "auto" }}>
        {/* Heading */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "64px" }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage
              id="landing_recruiter.prices.our_products"
              values={{ b: (chunks) => <>{chunks}</> }}
            />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsl(220, 10%, 46%)",
              fontSize: "1.125rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              m: 0,
            }}
          >
            <FormattedMessage id="landing_recruiter.prices.our_products_subtitle" />
          </Box>
        </Box>

        {/* Plan cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: "24px", lg: "32px" },
          }}
        >
          {plans.map((plan, i) => (
            <Box
              key={plan.name}
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              sx={{
                position: "relative",
                bgcolor: "hsl(0, 0%, 100%)",
                border: plan.popular
                  ? "1px solid hsl(145, 63%, 42%)"
                  : "1px solid hsl(220, 13%, 91%)",
                borderRadius: "1rem",
                p: "32px",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s",
                transform: plan.popular ? "scale(1.02)" : "none",
                boxShadow: plan.popular
                  ? "0 3px 5px hsla(145, 63%, 42%, 0.1), 0 4px 6px -4px hsla(145, 63%, 42%, 0.1)"
                  : "none",
                "&:hover": {
                  borderColor: plan.popular
                    ? "hsl(145, 63%, 42%)"
                    : "hsla(145, 63%, 42%, 0.3)",
                },
              }}
            >
              {/* Popular badge */}
              {plan.popular && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: "-12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    bgcolor: "hsl(145, 63%, 42%)",
                    color: "hsl(0, 0%, 100%)",
                    px: "16px",
                    py: "4px",
                    borderRadius: "9999px",
                  }}
                >
                  <FormattedMessage id="landing_recruiter.prices.popular" />
                </Box>
              )}

              {/* Plan name */}
              <Box
                component="h3"
                sx={{
                  fontSize: "1.25rem",
                  fontFamily: "Segoe UI Black, system-ui, sans-serif",
                  color: "hsl(220, 25%, 12%)",
                  mb: showPrices ? "8px" : "24px",
                  mt: 0,
                }}
              >
                <FormattedMessage id={plan.titleKey} />
              </Box>

              {showPrices && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-start",
                    mb: "24px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1.75rem",
                      fontFamily: "Segoe UI Black, system-ui, sans-serif",
                      color: "hsl(153, 68%, 25%)",
                    }}
                  >
                    <FormattedMessage id={plan.priceKey} />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      color: "hsl(220, 10%, 46%)",
                      ml: "6px",
                    }}
                  >
                    <FormattedMessage id="landing_recruiter.per_year" />
                  </Box>
                </Box>
              )}

              {/* Features list */}
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  mb: "32px",
                  flex: 1,
                }}
              >
                <FormattedMessage
                  id={plan.bulletpointsKey}
                  values={{
                    li: (chunks) => {
                      const hasBold = Array.isArray(chunks)
                        ? chunks.some((c) => typeof c !== "string")
                        : typeof chunks !== "string";
                      return (
                        <BulletItem isBold={hasBold}>
                          {chunks}
                        </BulletItem>
                      );
                    },
                    b: (chunks) => (
                      <Box
                        component="b"
                        sx={{
                          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                        }}
                      >
                        {chunks}
                      </Box>
                    ),
                  }}
                />

                {/* Offer bulletpoints */}
                {plan.offerBulletpointsKey && (
                  <Box sx={{ mt: "32px" }}>
                    <FormattedMessage
                      id={plan.offerBulletpointsKey}
                      values={{
                        li: (chunks) => (
                          <OfferBulletItem key={String(chunks)}>
                            {chunks}
                          </OfferBulletItem>
                        ),
                        b: (chunks) => (
                          <Box
                            component="b"
                            sx={{
                              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                            }}
                          >
                            {chunks}
                          </Box>
                        ),
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* CTA button */}
              <Button
                component="a"
                href={useMailto ? "mailto:jules@squarehub.eu?subject=Order%20License" : `/${i18nLocale}/recruiters/sign_up`}
                fullWidth
                sx={{
                  bgcolor: plan.popular
                    ? "hsl(153 68% 25%)"
                    : "transparent",
                  color: plan.popular
                    ? "white"
                    : "hsl(220, 25%, 12%)",
                  border: plan.popular
                    ? "none"
                    : "1px solid hsl(220, 13%, 91%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  px: "1.5rem",
                  py: "0.875rem",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  textDecoration: "none",
                  "&:hover": {
                    bgcolor: plan.popular
                      ? "hsl(153 68% 25% / 0.9)"
                      : "hsla(220, 13%, 91%, 0.3)",
                    textDecoration: "none",
                  },
                }}
              >
                <FormattedMessage id="landing_recruiter.prices.selection_button" />{" "}
                <ArrowRight size={14} className="arrow-icon" />
              </Button>
            </Box>
          ))}
        </Box>

        {/* Footer note */}
        <Box
          component="p"
          sx={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "hsl(220, 10%, 46%)",
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            mt: "24px",
            mb: 0,
          }}
        >
          <FormattedMessage id="landing_recruiter.vat_excluded" />
        </Box>
      </Box>
    </Box>
  );
};

export default PricingSection;
